<template>
  <div ref="posterRoot" class="match-share-poster">
    <div class="main-card">
      <div class="teams-line">
        <div class="team-item">
          <ShareAvatarBadge :short-name="teamAName" :image-url="teamALogo" :size="25" class="team-logo" />
          <strong>{{ teamAName }}</strong>
        </div>
        <div class="center-time">{{ matchCountdown }}</div>
        <div class="team-item right">
          <strong>{{ teamBName }}</strong>
          <ShareAvatarBadge :short-name="teamBName" :image-url="teamBLogo" :size="25" class="team-logo" />
        </div>
      </div>
      <div class="teams-line-container">


        <div class="meta-line">
          <span>Rs. ₹0</span>
          <span>Guaranteed</span>
        </div>
        <div class="stats-line">
          <div class="stat-item">
            <span class="label">Max Prize Pool</span>
            <strong>{{ formatMoney(data.roomInfo?.prizePoolAmount) }}</strong>
          </div>
          <div class="stat-item">
            <span class="label">Spots</span>
            <strong>{{ data.roomInfo?.maxTeams || 0 }}</strong>
          </div>
          <div class="stat-item align-right">
            <span class="label">Entry</span>
            <strong>{{ formatMoney(data.roomInfo?.entryFee) }}</strong>
          </div>
        </div>
      </div>




      <SharePosterInviteFooter :user-name="displayUser" :team-name="displayUserTeamName"
        :match-text="`${teamAName} vs ${teamBName}`" :invite-code="data.inviteInfo?.invCode || ''"
        :qr-data-url="data.qrDataUrl || ''" />
    </div>
  </div>
</template>

<script setup>
import ShareAvatarBadge from '@/components/share/ShareAvatarBadge.vue'
import SharePosterInviteFooter from '@/components/share/SharePosterInviteFooter.vue'
import { currency, formatMatchTime } from '@/utils/index.js'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
})

const posterRoot = ref(null)
const timeTick = ref(0)
let timer = null

const formatMoney = (value) => {
  return currency(Number(value || 0), '₹', 2)
}

const teamAName = computed(() => dataSafe.value.matchInfo?.teamAShort || 'MI')
const teamBName = computed(() => dataSafe.value.matchInfo?.teamBShort || 'CSK')
const teamALogo = computed(() => {
  return dataSafe.value.matchInfo?.teamALogo
    || dataSafe.value.matchInfo?.teamaLogo
    || dataSafe.value.matchInfo?.teama?.logoUrl
    || ''
})
const teamBLogo = computed(() => {
  return dataSafe.value.matchInfo?.teamBLogo
    || dataSafe.value.matchInfo?.teambLogo
    || dataSafe.value.matchInfo?.teamb?.logoUrl
    || ''
})

const dataSafe = computed(() => props.data || {})

const matchCountdown = computed(() => {
  timeTick.value
  const start = Number(
    dataSafe.value.matchInfo?.timestampStart
    || dataSafe.value.matchInfo?.matchStartTime
    || dataSafe.value.roomInfo?.startTime
    || 0,
  )
  if (!start) return 'Upcoming'
  const parsedStart = start > 9999999999 ? start : start * 1000
  const status = Number(dataSafe.value.matchInfo?.status || dataSafe.value.matchInfo?.matchStatus || 0)
  const formatted = formatMatchTime(parsedStart, status)
  const dateText = String(formatted?.date || '').trim()
  const timeText = String(formatted?.time || '').trim()
  if (!dateText && !timeText) return 'Upcoming'
  if (dateText === 'Today' || dateText === 'Tomorrow') return `${dateText} ${timeText}`.trim()
  if (/^\d+[hms]/i.test(dateText) || /h\s*:/i.test(dateText)) return `${dateText} left`
  return dateText || timeText
})

const displayUser = computed(() => {
  const text = dataSafe.value.userInfo?.nickName || 'User'
  return text.length > 14 ? `${text.slice(0, 14)}...` : text
})
const displayUserTeamName = computed(() => {
  return String(dataSafe.value.teamMeta?.teamName || '').trim()
})

const getPosterElement = () => posterRoot.value

onMounted(() => {
  timer = setInterval(() => {
    timeTick.value += 1
  }, 10000)
})

onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
})

defineExpose({
  getPosterElement,
})
</script>

<style scoped lang="less">
.match-share-poster {
  width: 100%;
  background: transparent;
  overflow: hidden;
  color: #202b3c;
  display: flex;
  justify-content: center;
  align-items: center;

  .main-card {
    background: #18212e;
    width: 90%;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #dfe4ea;
    box-shadow: 0 6px 14px rgba(32, 43, 60, 0.12);

    .teams-line-container {
      background: #f5f5f5;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }

    .teams-line {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 14px;
      border-bottom: 1px solid #efeff2;
      background: #ffffff;

      .team-item {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 72px;

        &.right {
          justify-content: flex-end;
        }

        strong {
          font-size: 14px;
          color: #202b3c;
          line-height: 20px;
          font-weight: 800;
        }
      }

      .team-logo {
        width: 25px;
        height: 25px;
        border-radius: 50%;
        border: 1px solid #e5e9ef;
        background: #fff;
        object-fit: contain;
      }

      .center-time {
        font-size: 14px;
        line-height: 20px;
        color: #d82d2c;
        font-weight: 700;
        white-space: nowrap;
      }
    }

    .meta-line {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      line-height: 16px;
      color: #8a93a1;
      padding: 10px 14px 2px;
    }

    .stats-line {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      padding: 0 14px 12px;

      .stat-item {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .label {
          font-size: 11px;
          color: #8a93a1;
          line-height: 14px;
        }

        strong {
          font-size: 11px;
          color: #202b3c;
          line-height: 18px;
        }

        &.align-right {
          text-align: right;
        }
      }
    }

  }
}
</style>
